import { useLazyQuery, useMutation } from "@apollo/client";
import {
  clientEntities,
  clientNeoBanks,
  clientNeoFact,
  clientNeoSociete,
} from "@neonumy/api-clients";
import { FormInstance } from "antd";
import {
  CREATE_CLIENT_INFO,
  GET_CREATED_CLIENT_INFO,
  LIST_CLIENT_BANK_ACCOUNTS,
  LIST_PAYMENT_PERIODS,
  LIST_SEPA_ORDERS_COUNT,
  UPDATE_CLIENT_INFO,
} from "queries/centralApi/clients";
import { CREATE_ENTITY_ADDRESS, DELETE_CLIENT, GET_CLIENT_SIMPLE } from "queries/entities/clients";
import { GET_CLIENTS_XLSX, LIST_COMPANIES_SOCIETE } from "queries/neosociete/companies";
import {
  ClientBase,
  CreateEntityAddressInput,
  CreateUpdateEntityAddressOutput,
  DeleteClientInput,
  DeleteClientOutput,
  GetClientSimpleOutput,
} from "queries/types/entities/clients";
import { useEffect, useMemo, useState } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";

export default function useClientsApi(id?: ID, form?: FormInstance) {
  const [selectedClientsTable, setSelectedClientsTable] = useState<ClientBase[]>([]);

  const [listPaymentPeriods, { data: listPaymentPeriodsData, error: listPaymentPeriodsError }] =
    useLazyQuery<ListPaymentPeriodsResponse>(LIST_PAYMENT_PERIODS, {
      client: clientNeoFact,
    });

  useEffect(() => {
    if (listPaymentPeriodsError) onErrorGraphql(listPaymentPeriodsError);
  }, [listPaymentPeriodsError]);

  const listPaymentPeriodsOptions = listPaymentPeriodsData?.listPaymentPeriods ?? [];

  const [getClientInfo, { data: getClientInfoData, error: getClientInfoError }] =
    useLazyQuery<GetClientSimpleOutput>(GET_CLIENT_SIMPLE, {
      client: clientEntities,
    });

  useEffect(() => {
    if (getClientInfoError) onErrorGraphql(getClientInfoError);
  }, [getClientInfoError]);

  useEffect(() => {
    const getEntity = getClientInfoData?.getEntity;
    if (!getEntity) return;

    setSelectedClientsTable(prev => {
      const exists = prev?.find(client => client.id === getEntity.id);
      if (!exists) {
        return [...prev, getEntity as ClientBase];
      }
      return prev;
    });
  }, [getClientInfoData]);

  const [
    getCreatedClientInfo,
    { data: getCreatedClientInfoData, error: getCreatedClientInfoError },
  ] = useLazyQuery<GetCreatedClientInfoResponse, GetCreatedClientInfoVars>(
    GET_CREATED_CLIENT_INFO,
    {
      client: clientNeoFact,
    },
  );

  useEffect(() => {
    if (getCreatedClientInfoError) onErrorGraphql(getCreatedClientInfoError);
  }, [getCreatedClientInfoError]);

  useEffect(() => {
    const data = getCreatedClientInfoData?.listClientsInfo?.results?.[0];
    if (!data) return;

    form?.setFields([
      {
        name: "client",
        value: {
          label: `${data.code} - ${data.name}`,
          value: data.id,
          paymentMethod: data?.paymentMethod,
          vatType: data?.clientConfig?.clientVatType,
          currency: data?.clientConfig?.currency?.code,
          paymentDeadline: data?.clientConfig?.paymentDeadline,
        },
      },
    ]);
  }, [getCreatedClientInfoData, form]);

  const [
    createUpdateClient,
    { loading: createUpdateClientLoading, error: createUpdateClientError },
  ] = useMutation<
    CreateUpdateClientInfoMutationResponse,
    CreateClientInfoVars | UpdateClientInfoVars
  >(id ? UPDATE_CLIENT_INFO : CREATE_CLIENT_INFO, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (createUpdateClientError) onErrorGraphql(createUpdateClientError);
  }, [createUpdateClientError]);

  const [deleteClient, { loading: deleteClientLoading, error: deleteClientError }] = useMutation<
    DeleteClientOutput,
    DeleteClientInput
  >(DELETE_CLIENT, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (deleteClientError) onErrorGraphql(deleteClientError);
  }, [deleteClientError]);

  const [
    createEntityAddress,
    { loading: createEntityAddressLoading, error: createEntityAddressError },
  ] = useMutation<CreateUpdateEntityAddressOutput, CreateEntityAddressInput>(
    CREATE_ENTITY_ADDRESS,
    {
      client: clientEntities,
    },
  );

  useEffect(() => {
    if (createEntityAddressError) onErrorGraphql(createEntityAddressError);
  }, [createEntityAddressError]);

  const [
    listCompaniesSociete,
    {
      loading: listCompaniesSocieteLoading,
      data: listCompaniesSocieteData,
      error: listCompaniesSocieteError,
    },
  ] = useLazyQuery<ListCompaniesSocieteResponse, ListCompaniesSocieteVars>(LIST_COMPANIES_SOCIETE, {
    client: clientNeoSociete,
  });

  useEffect(() => {
    if (listCompaniesSocieteError) onErrorGraphql(listCompaniesSocieteError);
  }, [listCompaniesSocieteError]);

  const listCompaniesSocieteOptions = useMemo(
    () =>
      listCompaniesSocieteData?.listCompanies?.results?.map(item => ({
        label: item.name,
        value: item.name,
        ...item,
      })) ?? [],
    [listCompaniesSocieteData],
  );

  const [checkBankAccounts, { data: checkBankAccountsData, error: checkBankAccountsError }] =
    useLazyQuery<ListClientBankAccountsPaginatedResponse, ListClientBankAccountsPaginatedVars>(
      LIST_CLIENT_BANK_ACCOUNTS,
      {
        client: clientNeoBanks,
      },
    );

  useEffect(() => {
    if (checkBankAccountsError) onErrorGraphql(checkBankAccountsError);
  }, [checkBankAccountsError]);

  const hasMainActiveAccount =
    checkBankAccountsData?.listClientBankAccountsPaginated?.results?.some(
      account => account.isMain && account.isActive,
    ) ?? false;

  const [checkSepaOrders, { data: checkSepaOrdersData, error: checkSepaOrdersError }] =
    useLazyQuery<ListSepaOrdersCountResponse, ListSepaOrdersCountVars>(LIST_SEPA_ORDERS_COUNT, {
      client: clientNeoFact,
    });

  useEffect(() => {
    if (checkSepaOrdersError) onErrorGraphql(checkSepaOrdersError);
  }, [checkSepaOrdersError]);

  const hasSepaOrders = (checkSepaOrdersData?.listSepaOrders?.results?.length ?? 0) > 0;

  const [getClientsXlsx, { error: getClientsXlsxError }] = useLazyQuery<
    GetClientsXlsxResponse,
    GetClientsXlsxVars
  >(GET_CLIENTS_XLSX, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (getClientsXlsxError) onErrorGraphql(getClientsXlsxError);
  }, [getClientsXlsxError]);

  return {
    getClientInfo,
    hasSepaOrders,
    getClientsXlsx,
    checkSepaOrders,
    deleteClient,
    checkBankAccounts,
    listPaymentPeriods,
    createUpdateClient,
    deleteClientLoading,
    selectedClientsTable,
    hasMainActiveAccount,
    getCreatedClientInfo,
    listCompaniesSociete,
    setSelectedClientsTable,
    createEntityAddress,
    createUpdateClientLoading,
    listPaymentPeriodsOptions,
    listCompaniesSocieteOptions,
    listCompaniesSocieteLoading,
    createEntityAddressLoading,
  };
}
